import { IBusinessFullQuery } from "../../../interfaces/businessFull.interface";

// Canonical set of filters the business table panel can set. This is the
// source of truth that keeps the URL and Export in sync with the table — add a
// filter to BusinessTableFilters, add its key here, and the compile-time check
// below fails the build until it is also encoded in the arrays.
export type BusinessFilterKey =
  | "name"
  | "status"
  | "onlinePaymentStatus"
  | "complianceStatus"
  | "country"
  | "businessType"
  | "partnerId"
  | "features"
  | "acquirers"
  | "acquirerStatuses";

const SCALAR_FILTER_KEYS = [
  "name",
  "status",
  "onlinePaymentStatus",
  "complianceStatus",
  "country",
  "businessType",
  "partnerId",
  "acquirers",
  "acquirerStatuses",
] as const;

const ARRAY_FILTER_KEYS = ["features"] as const;

// Compile-time guard that the arrays and BusinessFilterKey stay in sync in both
// directions: a key missing from the arrays, or a stray array key that is not a
// real filter, makes this type non-`never` and fails the assignment to compile.
type EncodedFilterKey =
  (typeof SCALAR_FILTER_KEYS)[number] | (typeof ARRAY_FILTER_KEYS)[number];
type FilterKeyMismatch =
  | Exclude<BusinessFilterKey, EncodedFilterKey>
  | Exclude<EncodedFilterKey, BusinessFilterKey>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertFilterKeysInSync: FilterKeyMismatch extends never
  ? true
  : FilterKeyMismatch = true;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;
// Page sizes offered by the table's size changer. A URL can only select one of
// these, so a hand-edited ?pageSize=5000 can't ask getAll for an unbounded page.
export const BUSINESS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
// antd's default changer offered 20 before the options above, and shared links
// still carry it. Honour it from the URL without offering it in the changer.
const LEGACY_PAGE_SIZES = [20];

export interface BusinessTablePagination {
  current: number;
  pageSize: number;
}

export function readBusinessFiltersFromParams(
  params: URLSearchParams
): Partial<IBusinessFullQuery> {
  const filters: Record<string, unknown> = {};
  SCALAR_FILTER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value !== null && value !== "") filters[key] = value;
  });
  ARRAY_FILTER_KEYS.forEach((key) => {
    const values = params.getAll(key).filter((v) => v !== "");
    if (values.length > 0) filters[key] = values;
  });
  return filters as Partial<IBusinessFullQuery>;
}

export function readBusinessPaginationFromParams(
  params: URLSearchParams
): BusinessTablePagination {
  const current = Number(params.get("currentPage"));
  const pageSize = Number(params.get("pageSize"));
  return {
    current: current > 0 ? current : DEFAULT_PAGE,
    pageSize:
      BUSINESS_PAGE_SIZE_OPTIONS.includes(pageSize) ||
      LEGACY_PAGE_SIZES.includes(pageSize)
        ? pageSize
        : DEFAULT_PAGE_SIZE,
  };
}

/**
 * The page that still holds the first row the user was looking at after the
 * page size changes. antd keeps the old page number instead, so page 3 at 10
 * rows (21-30) would become page 3 at 100 rows (201-300).
 */
export function pageAfterSizeChange(
  previous: BusinessTablePagination,
  nextPageSize: number
): number {
  const firstRowIndex = (previous.current - 1) * previous.pageSize;
  return Math.floor(firstRowIndex / nextPageSize) + 1;
}

export function buildBusinessSearchParams(
  filters: Partial<IBusinessFullQuery>,
  pagination?: Partial<BusinessTablePagination>
): URLSearchParams {
  const params = new URLSearchParams();
  SCALAR_FILTER_KEYS.forEach((key) => {
    const value = (filters as Record<string, unknown>)[key];
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  ARRAY_FILTER_KEYS.forEach((key) => {
    const value = (filters as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      value
        .filter(
          (entry) => entry !== undefined && entry !== null && entry !== ""
        )
        .forEach((entry) => params.append(key, String(entry)));
    }
  });
  // Keep defaults out of the URL so a pristine list stays at "/businesses".
  if (pagination?.current && pagination.current !== DEFAULT_PAGE) {
    params.set("currentPage", String(pagination.current));
  }
  if (pagination?.pageSize && pagination.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set("pageSize", String(pagination.pageSize));
  }
  return params;
}

/**
 * A hidden acquirer must not reach the request, not even from a bookmarked URL —
 * the release gate has to hold on the wire, not only in the dropdown.
 */
export function pruneHiddenAcquirers(
  selected: string[],
  visible: string[]
): string[] {
  const allowed = new Set(visible);
  return selected.filter((value) => allowed.has(value));
}
